/**
 * API Hooks for Settings
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@/lib/api-client';
import { useAuth, useAuthTokenReady } from './use-auth';

export interface ModelSettings {
  chatModel?: {
    provider: string;
    apiKey?: string;
    baseUrl?: string;
    modelName?: string;
    projectId?: string;
  };
  embeddingModel?: {
    provider: string;
    model: string;
  };
}

export interface McpServerConfig {
  id?: string;
  name: string;
  cmd: string;
  args: string[];
  enabled?: boolean;
  description?: string;
  defaultTool?: string;
}

export interface Settings {
  id: number;
  model_settings?: ModelSettings;
  mcp_servers?: McpServerConfig[];
  [key: string]: any;
}

export const settingsKeys = {
  all: ['settings'] as const,
  detail: () => [...settingsKeys.all, 'detail'] as const,
};

/**
 * Hook to fetch settings
 */
export function useSettings() {
  const apiClient = getApiClient();
  const { user, loading } = useAuth();
  const tokenReady = useAuthTokenReady();

  return useQuery<Settings>({
    queryKey: settingsKeys.detail(),
    queryFn: async () => {
      const response = await apiClient.request<{ settings: Settings }>('/admin/settings');
      return response.settings;
    },
    enabled: !loading && !!user && tokenReady,
  });
}

/**
 * Hook to update settings
 */
export function useUpdateSettings() {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();

  return useMutation<
    Settings,
    Error,
    { model_settings?: ModelSettings; mcp_servers?: McpServerConfig[] }
  >({
    mutationFn: async (payload) => {
      const response = await apiClient.request<{ settings: Settings }>(
        '/admin/settings',
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        }
      );
      return response.settings;
    },
    onSuccess: () => {
      // Invalidate settings query to refetch
      queryClient.invalidateQueries({ queryKey: settingsKeys.detail() });
    },
  });
}




